import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    CircularProgress,
    Alert,
    TextField,
    IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import Pagination from '@mui/material/Pagination';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

interface PortfolioItem {
    _id: string;
    title: string;
    description?: string;
    modelIds?: string[];
    preview?: string;
}

const PortfolioRegistryPage = () => {
    const [items, setItems] = useState<PortfolioItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [totalPages, setTotalPages] = useState(1);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const res = await api.get('/api/admin/portfolio', {
                    params: { page, limit, search },
                });
                setItems(res.data.data);
                setTotalPages(res.data.pages);
            } catch (err) {
                console.error(err);
                setError('Ошибка при загрузке портфолио');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [page, limit, search]);

    const handleAddClick = () => navigate('/portfolio/create');

    const handleDelete = async (id: string) => {
        if (!window.confirm('Удалить запись портфолио?')) return;

        try {
            await api.delete(`/api/admin/portfolio/${id}`);
            setItems(prev => prev.filter(item => item._id !== id));
        } catch (err) {
            console.error(err);
            setError('Не удалось удалить запись');
        }
    };

    return (
        <Box p={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">Портфолио</Typography>
                <Button variant="contained" color="primary" onClick={handleAddClick}>
                    Добавить работу
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box mb={2}>
                <TextField
                    label="Поиск по заголовку"
                    size="small"
                    value={search}
                    onChange={e => {
                        setPage(1);
                        setSearch(e.target.value);
                    }}
                    sx={{ width: 300 }}
                />
            </Box>

            {loading ? (
                <CircularProgress />
            ) : items.length === 0 ? (
                <Typography>Записей портфолио пока нет.</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>№</TableCell>
                                <TableCell>Заголовок</TableCell>
                                <TableCell>Марка/модель (id)</TableCell>
                                <TableCell>Изображение</TableCell>
                                <TableCell>Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow
                                    key={item._id}
                                    hover
                                    onClick={() => navigate(`/portfolio/${item._id}`)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                                    <TableCell>{item.title}</TableCell>
                                    <TableCell>
                                        {item.modelIds && item.modelIds.length > 0
                                            ? item.modelIds.join(', ')
                                            : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {item.preview ? 'Да' : 'Нет'}
                                    </TableCell>
                                    <TableCell onClick={e => e.stopPropagation()}>
                                        <IconButton color="error" onClick={() => handleDelete(item._id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                />
            </Box>
        </Box>
    );
};

export default PortfolioRegistryPage;
